/**
 * PEM/DER handling for the import ceremony — pure, so it can be pinned without a browser.
 *
 * ── WHY THE BROWSER HAS TO DO THIS AT ALL ───────────────────────────────────
 *
 * The server accepts PEM only (contract §4), and the file an AD administrator actually has in hand is
 * usually DER: `certutil -ca.cert`, the "Copy to File" wizard's default, and `.cer` straight out of
 * certsrv all write binary. Refusing that file with "must be PEM" would send the person holding the
 * certificate away to run an openssl command they may not have — for a difference in encoding that
 * carries no information. So the file is wrapped here, byte for byte, and nothing else about it is
 * touched: the DER bytes inside the block are exactly the bytes read from disk, and the server parses
 * and validates them (PureID never rewrites, re-encodes or "tidies" the customer's
 * certificate).
 *
 * NOTHING HERE PARSES X.509. Client-side parsing would be display-only in any case — the server's
 * verdict is the truth, and no client-derived field may gate the Continue button (design record, stage 3
 * review, architect note). Wrapping is transport, not inspection.
 */

/** The 64-character line width RFC 7468 §2 requires of a PEM body. */
const PEM_LINE = 64;

/**
 * Base64 for an ArrayBuffer, without pulling in a dependency.
 *
 * CHUNKED, because `String.fromCharCode(...bytes)` spreads one argument per byte and blows the call
 * stack on anything past a few tens of kilobytes — a PKCS#12 with a chain is comfortably past that, so
 * the naive form fails on exactly the files this ceremony exists to accept.
 */
export function toBase64(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  const CHUNK = 0x8000;
  let binary = "";
  for (let i = 0; i < view.length; i += CHUNK) {
    binary += String.fromCharCode(...view.subarray(i, i + CHUNK));
  }
  return btoa(binary);
}

/**
 * A certificate file as PEM.
 *
 * A string input that already carries a BEGIN marker is returned UNCHANGED — a PEM bundle may hold
 * several concatenated certificates, and re-wrapping it would corrupt every block after the first.
 * Anything else is DER and is wrapped.
 */
export function toPem(bytes: ArrayBuffer | string): string {
  if (typeof bytes === "string") {
    return bytes.includes("-----BEGIN") ? bytes : wrap(bytes.replace(/\s+/g, ""));
  }
  const text = decodeUtf8(bytes);
  if (text !== null && text.includes("-----BEGIN")) return text;
  return wrap(toBase64(bytes));
}

/** The PEM block itself: header, 64-character lines, footer, trailing newline. */
function wrap(base64: string): string {
  const lines: string[] = [];
  for (let i = 0; i < base64.length; i += PEM_LINE) {
    lines.push(base64.slice(i, i + PEM_LINE));
  }
  return `-----BEGIN CERTIFICATE-----\n${lines.join("\n")}\n-----END CERTIFICATE-----\n`;
}

/**
 * The bytes as text, or null when they are not valid UTF-8.
 *
 * FATAL DECODING IS THE POINT. A DER certificate is arbitrary binary and will usually contain byte
 * sequences no UTF-8 decoder accepts; a lenient decode would replace those with U+FFFD and hand back a
 * string that contains no BEGIN marker but is also no longer the file. Throwing tells us "this is
 * binary" without guessing, and the caller falls through to base64 of the ORIGINAL buffer.
 */
function decodeUtf8(bytes: ArrayBuffer): string | null {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}
